import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Link,
  Alert,
  AlertIcon,
  Container,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
  Card,
  CardBody,
  Heading,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import RotaryLogo from './RotaryLogo';
import ProductName from './ProductName';

const Auth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    showPassword: false,
  });
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('signin');
  const supabase = useSupabaseClient();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    console.log('Starting authentication process...');

    try {
      if (mode === 'signin') {
        console.log('Attempting sign in with:', { email: formData.email });
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });
        console.log('Sign in response:', { data, error });
        if (error) throw error;
        
        // Check if we got a session
        const session = await supabase.auth.getSession();
        console.log('Current session:', session);

        // If we have a successful login, redirect
        if (data?.user) {
          if (!data.user.email_confirmed_at) {
            setError('Please verify your email before logging in');
            return;
          }
          console.log('Login successful, redirecting to /send-messages');
          navigate('/send-messages', { replace: true });
          return;
        }
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: `${window.location.origin}/verify`
          }
        });

        if (signUpError) {
          throw signUpError;
        }

        // Redirect to verification pending page
        navigate('/verification-pending', { replace: true });
      }
    } catch (error) {
      console.error('Authentication error:', error);
      if (error.message.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else if (error.message.includes('Email not confirmed')) {
        setError('Please verify your email before logging in');
      } else if (error.message.includes('User already registered')) {
        setError('Email already registered');
      } else {
        setError('An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError(null);
    setFormData({
      email: '',
      password: '',
      showPassword: false,
    });
  };

  return (
    <Box minH="100vh" bg="gray.50" py={4}>
      <Container maxW="md">
        <VStack spacing={4} align="center">
          <VStack spacing={2} align="center">
            <RotaryLogo containerWidth={280} />
            <Box w="full" textAlign="center">
              <ProductName />
            </Box>
          </VStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          <Box
            bg="white"
            p={6}
            borderRadius="lg"
            boxShadow="lg"
            w="full"
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <Heading size="md" color="gray.700">
                  {mode === 'signin' ? 'Sign In' : 'Create Account'}
                </Heading>

                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={formData.showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter your password"
                      minLength={6}
                    />
                    <InputRightElement>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={formData.showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setFormData({ ...formData, showPassword: !formData.showPassword })}
                        aria-label={formData.showPassword ? 'Hide password' : 'Show password'}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  width="100%"
                  isLoading={loading}
                >
                  {mode === 'signin' ? 'Sign In' : 'Sign Up'}
                </Button>

                {mode === 'signin' && (
                  <Link color="blue.500" onClick={() => navigate('/forgot-password')}>
                    Forgot Password?
                  </Link>
                )}

                <Text>
                  {mode === 'signin'
                    ? "Don't have an account? "
                    : 'Already have an account? '}
                  <Link
                    color="blue.500"
                    onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                  >
                    {mode === 'signin' ? 'Sign Up' : 'Sign In'}
                  </Link>
                </Text>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Auth;
